import React, { useState, useEffect } from "react";
import TalkToAgentCards from "./TalkToAgent";
import { Agent } from "./Agent";
import HeroSection from "./master";
import Footer from "./footer";
import Header from "./Header";
import BgAgentCard from "./assests/BgAgentCard.png";
import CtaBg from "./assests/CTABG.png";

interface Feature {
  id: number;
  text: string;
}

interface CardData {
  cardTag: string;
  cardTitle: string;
  features: Feature[];
  profilePhoto: string;
  agentsArray: Agent[];
}

interface Category {
  id: number;
  name: string;
  key: string;
}

interface AgentCardsPageProps {
  initialCategory?: number;
}

type CardDataMap = {
  [key: string]: CardData[];
};

const VOICEX_URL_CONSTANTS = {
  "voicex-us": "https://external-proxy-us.nurixlabs.tech",
  "voicex-in": "https://external-proxy.nurixlabs.tech",
  "voicex-stage": "https://agentx.stage.nurixlabs.tech"
};
const DEFAULT_USER_ID = "40d323bc-dc17-4b3d-a7ac-8ded8a0b16ff";

// The sample agent object you provided

// Main component
const AgentCardsPage: React.FC<AgentCardsPageProps> = ({
  initialCategory = 0
}) => {
  // Function to get category from URL parameters
  const getCategoryFromURL = (): number => {
    if (typeof window !== "undefined") {
      const urlParams = new URLSearchParams(window.location.search);
      const categoryParam = urlParams.get("category");
      return categoryParam ? parseInt(categoryParam, 10) : initialCategory;
    }
    return initialCategory;
  };

  const [selectedCategory, setSelectedCategory] = useState<number>(
    getCategoryFromURL()
  );

  // Update category when URL changes
  useEffect(() => {
    const handleURLChange = () => {
      setSelectedCategory(getCategoryFromURL());
    };

    // Listen for browser back/forward navigation
    window.addEventListener("popstate", handleURLChange);

    // Also check on component mount
    handleURLChange();

    return () => {
      window.removeEventListener("popstate", handleURLChange);
    };
  }, [initialCategory]);

  // Function to update URL when category changes
  const updateURLAndCategory = (categoryId: number) => {
    setSelectedCategory(categoryId);

    // Update URL without page refresh
    const url = new URL(window.location.href);
    if (categoryId === 0) {
      url.searchParams.delete("category");
    } else {
      url.searchParams.set("category", categoryId.toString());
    }
    window.history.pushState({}, "", url.toString());
  };

  // Category configuration - matches your Categories page IDs
  const categories: Category[] = [
    { id: 0, name: "All", key: "all" },
    { id: 1, name: "Insurance", key: "insurance" },
    { id: 2, name: "Retail", key: "retail" },
    { id: 3, name: "Financial Services", key: "financial" },
    { id: 4, name: "Education", key: "education" },
    { id: 5, name: "Real Estate", key: "realestate" },
    { id: 6, name: "Health & Fitness", key: "health" },
    { id: 7, name: "Home Services", key: "home" },
    { id: 8, name: "Miscellaneous", key: "misc" }
  ];

  // Updated sample data with your exact agent object
  const cardData: CardDataMap = {
    insurance: [
      {
        cardTag: "Health Insurance",
        cardTitle: "Ask Your Policy",
        features: [
          { id: 1, text: "Policy Info" },
          { id: 2, text: "Benefit Details" },
          { id: 3, text: "Download Docs" }
        ],
        profilePhoto:
          "https://images.unsplash.com/photo-1494790108755-2616b612b123?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80",
        agentsArray: [
          // Example agent object matching the Agent interface
          {
            id: "ece29c56-d521-4a47-8278-a13dba90d83a",
            name: "Priya",
            description:
              "The agent explains policy benefits, cost implications, and support options for ongoing medical needs.",
            subDescription:
              "The agent explains policy benefits, cost implications, and support options for ongoing medical needs.",
            agentName: "Ask Your Policy",
            icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female5.png",
            chatIcon:
              "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female5.png-chat",
            featureList: [
              "Clarifies details of the policy",
              "Explains the payment breakdown"
            ],
            voice: "true",
            chat: "false",
            callTitle: "Ask Your Policy Agent",
            chatHints: [],
            callHintsTable: [],
            callHints: ["Name: Rajesh Kumar", "DOB: February 15, 1989"],
            voiceInputs: [],
            voiceProvider: "voicex",
            try_speaking: [
              "क्या आप मुझे समझा सकती हैं कि अब हमारे out-of-pocket costs कैसे बदलेंगे जबकि हर family member ने अपना individual deductible meet कर लिया है?",
              "क्या कोई medical programs या support services available हैं diabetes जैसी ongoing conditions को manage करने के लिए?",
              "क्या आप clarify कर सकती हैं कि coverage कैसे काम करता है अगर हमें कभी अपने service area के बाहर emergency care की ज़रूरत पड़े?"
            ],
            activities: [],
            phoneNumber: "+1-800-000-0000",
            language: "Hinglish",
            default_voiceId: "default",
            voiceConfig: {
              baseUrl: VOICEX_URL_CONSTANTS["voicex-in"],
              user_id: "idn_2zXrDB1ZzqaM574PE2IOlSiulmJ",
              agent_id: "ece29c56-d521-4a47-8278-a13dba90d83a",
              tenant_id: "",
              workspace_id: "93debacb-6338-4fe3-befb-731e7fd94a3b",
              overide_previous_context: true
            },
            optional_voices: [
              {
                id: "default",
                name: "Priya",
                icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female5.png",
                description:
                  "The agent explains policy benefits, cost implications, and support options for ongoing medical needs.",
                language: "Hinglish",
                region: "India"
              }
            ]
          },
          {
            id: "e6e4e8ac-ea58-4d6d-a308-35bfa2c2f5a3",
            name: "Myra",
            description:
              "Explains policy benefits, cost implications, and support options for ongoing medical needs.",
            subDescription:
              "Explains policy benefits, cost implications, and support options for ongoing medical needs.",
            agentName: "Ask Your Policy",
            icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female4.png",
            chatIcon:
              "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female4.png-chat",
            featureList: [
              "Clarify details of your policy",
              "Explain payment breakdown"
            ],
            voice: "true",
            chat: "false",
            callTitle: "Ask Your Policy",
            chatHints: [],
            callHintsTable: [],
            callHints: [
              "Name: Alex Smith",
              "Date of Birth: 15th February 1989(edited) "
            ],
            voiceInputs: [],
            voiceProvider: "voicex",
            try_speaking: [
              "Can you walk me through how my out-of-pocket costs will change now that each family member has met their individual deductible?",
              "Are there any medical programs or support services available for managing ongoing conditions like diabetes?",
              "Can you clarify how coverage works if we ever need emergency care outside our service area?"
            ],
            activities: [],
            phoneNumber: "+1-800-000-0000",
            language: "English",
            default_voiceId: "",
            voiceConfig: {
              baseUrl: "https://external-proxy-us.nurixlabs.tech",
              user_id: "idn_30B1uTyEU6DGDuC7Oio2RjPFWpE",
              agent_id: "e6e4e8ac-ea58-4d6d-a308-35bfa2c2f5a3",
              tenant_id: "",
              workspace_id: "8df3c72c-12a4-4b2b-8f6c-c15689e2cb8a",
              overide_previous_context: true
            },
            optional_voices: [
              {
                id: "default",
                name: "Marissa",
                icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female4.png",
                description:
                  "Assists with plan recommendations based on specific health and budget needs.",
                language: "English",
                region: "USA"
              }
            ]
          }
        ]
      },
      {
        cardTag: "Health Insurance",
        cardTitle: "Policy Sales",
        features: [
          { id: 1, text: "Suggest Plans" },
          { id: 2, text: "Explain Premiums" },
          { id: 3, text: "Start Application" }
        ],
        profilePhoto:
          "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80",
        agentsArray: [
          {
            id: "14c47d46-9883-46fd-b71f-1ab82326caf2",
            name: "Marissa",
            description:
              "Assists with plan recommendations based on specific health and budget needs.",
            subDescription:
              "Assists with plan recommendations based on specific health and budget needs.",
            agentName: "Lead Qualification",
            icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female4.png",
            chatIcon:
              "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female4.png-chat",
            featureList: [
              "Assess customer needs",
              "Suggest best suited policy"
            ],
            voice: "true",
            chat: "false",
            callTitle: "Lead Qualification Agent",
            chatHints: [],
            callHintsTable: [],
            callHints: [
              "User name: Mr. Smith",
              "Based out of: San Francisco, California",
              "Looking for: Health insurance policy for himself (35years old), his spouse Elena Smith( 33 years old), and his son Tony Smith (10 years old)"
            ],
            voiceInputs: [],
            voiceProvider: "voicex",
            try_speaking: [
              "I happen to have diabetes and Tony keeps having dental problems, do you have any policy which covers both?",
              "The cost is quite high, I wont be able to afford it. Can you provide more discount?",
              "I'd like to check across other providers before making a decision."
            ],
            activities: [],
            phoneNumber: "+1-800-000-0000",
            language: "English",
            default_voiceId: "",
            voiceConfig: {
              baseUrl: "https://external-proxy-us.nurixlabs.tech",
              user_id: "idn_30B1uTyEU6DGDuC7Oio2RjPFWpE",
              agent_id: "14c47d46-9883-46fd-b71f-1ab82326caf2",
              tenant_id: "",
              workspace_id: "8df3c72c-12a4-4b2b-8f6c-c15689e2cb8a",
              overide_previous_context: true
            },
            optional_voices: [
              {
                id: "default",
                name: "Marissa",
                icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female4.png",
                description:
                  "Assists with plan recommendations based on specific health and budget needs.",
                language: "English",
                region: "USA"
              }
            ]
          }
        ]
      },
      {
        cardTag: "P&C Insurance",
        cardTitle: "Renewals",
        features: [
          { id: 1, text: "Renewal Alerts" },
          { id: 2, text: "Upgrade Options" },
          { id: 3, text: "Instant Payment" }
        ],
        profilePhoto:
          "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80",
        agentsArray: [
          {
            id: "b2eb33f6-c6e9-4d8e-a66a-2df053b3a8ed",
            name: "Meera",
            description:
              "An agent who helps customers smoothly renew their auto insurance policies by verifying coverage details, explaining premium changes, and ensuring uninterrupted protection.",
            subDescription:
              "An agent who helps customers smoothly renew their auto insurance policies by verifying coverage details, explaining premium changes, and ensuring uninterrupted protection.",
            agentName: "Auto Insurance Renewal",
            icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female5.png",
            chatIcon:
              "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female5.png-chat",
            featureList: [
              "Confirms customer identity and availability for renewal conversation",
              "Collects up-to-date information on vehicle, address, listed drivers, and driving history",
              "Offers bundling opportunity for auto and home insurance"
            ],
            voice: "true",
            chat: "false",
            callTitle: "Auto Insurance Renewal",
            chatHints: [],
            callHintsTable: [],
            callHints: [],
            voiceInputs: [],
            voiceProvider: "voicex",
            try_speaking: [],
            activities: [],
            phoneNumber: "+1-800-000-0000",
            language: "Hinglish",
            default_voiceId: "default",
            voiceConfig: {
              baseUrl: VOICEX_URL_CONSTANTS["voicex-in"],
              user_id: "idn_2zXrDB1ZzqaM574PE2IOlSiulmJ",
              agent_id: "b2eb33f6-c6e9-4d8e-a66a-2df053b3a8ed",
              tenant_id: "",
              workspace_id: "93debacb-6338-4fe3-befb-731e7fd94a3b",
              overide_previous_context: true
            },
            optional_voices: [
              {
                id: "default",
                name: "Meera",
                icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female5.png",
                description:
                  "An agent who helps customers smoothly renew their auto insurance policies by verifying coverage details, explaining premium changes, and ensuring uninterrupted protection.",
                language: "Hinglish",
                region: "India"
              }
            ]
          },
          {
            id: "ff594c28-cbd4-4994-b3d1-5c85cde4f0f9",
            name: "Ethan",
            description:
              "A specialist who helps customers smoothly renew their auto insurance policies by verifying coverage details, explaining premium changes, and ensuring uninterrupted protection.",
            subDescription:
              "A specialist who helps customers smoothly renew their auto insurance policies by verifying coverage details, explaining premium changes, and ensuring uninterrupted protection.",
            agentName: "Auto Insurance Renewal",
            icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/male1.png",
            chatIcon:
              "https://d3udl6i7sipqjw.cloudfront.net/agent-images/male1.png-chat",
            featureList: [
              "Sales Qualification Policy",
              "Updates Collection",
              "Renewal Reminders"
            ],
            voice: "true",
            chat: "false",
            callTitle: "Auto Insurance Renewal",
            chatHints: [],
            callHintsTable: [],
            callHints: [
              "Name : John Doe",
              "Vehicle : 2021 Ford F - 150",
              "Address : 123 Main Street, New York"
            ],
            voiceInputs: [],
            voiceProvider: "voicex",
            try_speaking: [],
            activities: [],
            phoneNumber: "+1-800-000-0000",
            language: "English",
            default_voiceId: "",
            voiceConfig: {
              baseUrl: "https://external-proxy-us.nurixlabs.tech",
              user_id: "idn_30B1uTyEU6DGDuC7Oio2RjPFWpE",
              agent_id: "ff594c28-cbd4-4994-b3d1-5c85cde4f0f9",
              tenant_id: "",
              workspace_id: "8df3c72c-12a4-4b2b-8f6c-c15689e2cb8a",
              overide_previous_context: true
            },
            optional_voices: [
              {
                id: "default",
                name: "Ethan",
                icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/male1.png",
                description:
                  "A specialist who helps customers smoothly renew their auto insurance policies by verifying coverage details, explaining premium changes, and ensuring uninterrupted protection.",
                language: "English",
                region: "USA"
              }
            ]
          }
        ]
      },
      {
        cardTag: "P&C Insurance",
        cardTitle: "Lead Qualification",
        features: [
          { id: 1, text: "Screen Leads" },
          { id: 2, text: "Collect Risk Data" },
          { id: 3, text: "Assign Sales Rep" }
        ],
        profilePhoto:
          "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80",
        agentsArray: [
          {
            id: "658cae61-49e2-4a28-a9a1-4e4aa1190b1e",
            name: "Auto Insurance",
            description:
              "Alex is a warm, confident, and emotionally intelligent voice advisor for Nuplay Insurance, specializing in auto insurance lead qualification.",
            subDescription:
              "Alex is a warm, confident, and emotionally intelligent voice advisor for Nuplay Insurance, specializing in auto insurance lead qualification.",
            agentName: "Alex",
            icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/male1.png",
            chatIcon:
              "https://d3udl6i7sipqjw.cloudfront.net/agent-images/male1.png",
            featureList: [
              "Recommends named plans based on real-time inputs like age, driving history, and coverage needs.",
              "Handles objections with empathy while clearly highlighting the value of a free, no-pressure quote."
            ],
            voice: "true",
            chat: "false",
            callTitle: "Auto Insurance",
            chatHints: [],
            callHintsTable: [],
            callHints: ["ZIP : 12345"],
            voiceInputs: [],
            voiceProvider: "voicex",
            try_speaking: [
              "I am happy with my current insurance plan.",
              "I have multiple cars and need a full coverage. Which plan is best for me?"
            ],
            phoneNumber: "+1-800-000-0000",
            activities: [],
            language: "English",
            default_voiceId: "default",
            voiceConfig: {
              baseUrl: "https://external-proxy-us.nurixlabs.tech",
              user_id: "idn_30B1uTyEU6DGDuC7Oio2RjPFWpE",
              agent_id: "658cae61-49e2-4a28-a9a1-4e4aa1190b1e",
              tenant_id: "8df3c72c-12a4-4b2b-8f6c-c15689e2cb8a",
              workspace_id: "8df3c72c-12a4-4b2b-8f6c-c15689e2cb8a",
              overide_previous_context: true
            },
            optional_voices: [
              {
                id: "default",
                name: "Alex",
                icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/male1.png",
                description: "Male Warm & Confident",
                language: "English",
                region: "US"
              }
            ]
          }
        ]
      },
      {
        cardTag: "P&C Insurance",
        cardTitle: "FNOL",
        features: [
          { id: 1, text: "Capture Claim" },
          { id: 2, text: "Guide Docs" },
          { id: 3, text: "Send Updates" }
        ],
        profilePhoto:
          "https://images.unsplash.com/photo-1489424731084-a5d8b219a5bb?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80",
        agentsArray: [
          {
            id: "623b1cfc-901c-41fb-89dc-ea6d0f5ab153",
            name: "Priya",
            description:
              "An agent who guides homeowners through filing their first notice of loss claim after property damage, gathering essential details and initiating the claims process.",
            subDescription:
              "An agent who guides homeowners through filing their first notice of loss claim after property damage, gathering essential details and initiating the claims process.",
            agentName: "FNOL Agent",
            icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female5.png",
            chatIcon:
              "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female5.png-chat",
            featureList: [
              "Collects information about the incident",
              "Schedules an adjuster visit and confirms all appointment details clearly"
            ],
            voice: "true",
            chat: "false",
            callTitle: "FNOL Agent",
            chatHints: [],
            callHintsTable: [],
            callHints: [
              "Name: Amit Sharma",
              "Email: amit@gmail.com",
              "Phone Number: 9876543210"
            ],
            voiceInputs: [],
            voiceProvider: "voicex",
            try_speaking: [
              "Water leakage हुआ है kitchen में… wall पूरा गीला हो गया था।",
              "Incident परसों रात को हुआ था, और हाँ — plumber को emergency में बुलाना पड़ा था।"
            ],
            activities: [],
            phoneNumber: "+1-800-000-0000",
            language: "Hinglish",
            default_voiceId: "default",
            voiceConfig: {
              baseUrl: VOICEX_URL_CONSTANTS["voicex-in"],
              user_id: "idn_2zXrDB1ZzqaM574PE2IOlSiulmJ",
              agent_id: "623b1cfc-901c-41fb-89dc-ea6d0f5ab153",
              tenant_id: "",
              workspace_id: "93debacb-6338-4fe3-befb-731e7fd94a3b",
              overide_previous_context: true
            },
            optional_voices: [
              {
                id: "default",
                name: "Priya",
                icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female5.png",
                description:
                  "An agent who guides homeowners through filing their first notice of loss claim after property damage, gathering essential details and initiating the claims process.",
                language: "Hinglish",
                region: "India"
              }
            ]
          },
          {
            id: "86ed9282-c73f-4632-9627-d6d1392c8738",
            name: "Sarah",
            description:
              "A specialist who guides homeowners through filing their first notice of loss claim after property damage, gathering essential details and initiating the claims process.",
            subDescription:
              "A specialist who guides homeowners through filing their first notice of loss claim after property damage, gathering essential details and initiating the claims process.",
            agentName: "FNOL Agent",
            icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female2.png",
            chatIcon:
              "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female2.png-chat",
            featureList: [
              "Register home damage claim",
              "Collect incident details"
            ],
            voice: "true",
            chat: "false",
            callTitle: "FNOL Agent",
            chatHints: [],
            callHintsTable: [],
            callHints: [
              " Address: 123 Main Street, Springfield, Illinois, six two seven zero four, United States",
              "Policy ID: 100002396992",
              "Claim Number: C 123456",
              "Phone: 7012224839",
              "Email: joel.johnson@nurix.ai",
              "Amount: $1000",
              "Insurance Type: 3705"
            ],
            voiceInputs: [],
            voiceProvider: "voicex",
            try_speaking: [],
            activities: [],
            phoneNumber: "+1-800-000-0000",
            language: "English",
            default_voiceId: "",
            voiceConfig: {
              baseUrl: "https://external-proxy-us.nurixlabs.tech",
              user_id: "idn_30B1uTyEU6DGDuC7Oio2RjPFWpE",
              agent_id: "86ed9282-c73f-4632-9627-d6d1392c8738",
              tenant_id: "",
              workspace_id: "8df3c72c-12a4-4b2b-8f6c-c15689e2cb8a",
              overide_previous_context: true
            },
            optional_voices: [
              {
                id: "default",
                name: "Sarah",
                icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female2.png",
                description:
                  "A specialist who guides homeowners through filing their first notice of loss claim after property damage, gathering essential details and initiating the claims process.",
                language: "English",
                region: "USA"
              }
            ]
          }
        ]
      }
    ],
    retail: [
      {
        cardTag: "Multi-category",
        cardTitle: "Order Tracking & Return",
        features: [
          { id: 1, text: "Track Orders" },
          { id: 2, text: "Process Returns" },
          { id: 3, text: "Send Receipts" }
        ],
        profilePhoto:
          "https://images.unsplash.com/photo-1465101046530-73398c7f28ca?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80",
        agentsArray: [
          {
            id: "4a0f903f-02e5-45b0-93ce-fc94efc57d39",
            name: "Priya",
            description:
              "An agent that provides real-time order tracking and return process guidance.",
            subDescription:
              "An agent that provides real-time order tracking and return process guidance.",
            agentName: "Order Tracking Agent",
            icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female5.png",
            chatIcon:
              "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female5.png-chat",
            featureList: [
              "Track/Cancel order",
              "Change delivery instructions",
              "Exchange and refund status",
              "Return order"
            ],
            voice: "true",
            chat: "false",
            callTitle: "Order Tracking Agent",
            chatHints: [],
            callHintsTable: [],
            callHints: [
              "Name: Rajesh",
              "Email: rajesh@gmail.com",
              "Order ID 123: Wireless Headphones (Ordered)",
              "Order ID 456: Smart Home Security Camera (Shipped)",
              "Order ID 789: Smartphone Case (Out for Delivery)",
              "Order ID 101: Smartwatch (Refund)",
              "Order ID 202: Bluetooth Speaker (Delivered)"
            ],
            voiceInputs: [],
            voiceProvider: "voicex",
            try_speaking: [],
            activities: [],
            phoneNumber: "+1-800-000-0000",
            language: "Hinglish",
            default_voiceId: "default",
            voiceConfig: {
              baseUrl: VOICEX_URL_CONSTANTS["voicex-in"],
              user_id: "idn_2zXrDB1ZzqaM574PE2IOlSiulmJ",
              agent_id: "4a0f903f-02e5-45b0-93ce-fc94efc57d39",
              tenant_id: "",
              workspace_id: "93debacb-6338-4fe3-befb-731e7fd94a3b",
              overide_previous_context: true
            },
            optional_voices: [
              {
                id: "default",
                name: "Priya",
                icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female5.png",
                description:
                  "An agent that provides real-time order tracking and return process guidance.",
                language: "Hinglish",
                region: "India"
              }
            ]
          },

          {
            id: "bb9f0993-f38e-49be-aa6f-452b7895e5a3",
            name: "Alice",
            description:
              "An agent that provides real-time order tracking and return process guidance.",
            subDescription:
              "An agent that provides real-time order tracking and return process guidance.",
            agentName: "Order Tracking Agent",
            icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female2.png",
            chatIcon:
              "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female2.png-chat",
            featureList: [],
            voice: "true",
            chat: "false",
            callTitle: "Order Tracking Agent",
            chatHints: [],
            callHintsTable: [],
            callHints: [
              "Name: Joe",
              "Email: joe@gmail.com",
              "Order ID 123: Wireless Headphones (Ordered)",
              "Order ID 456: Smart Home Security Camera (Shipped)",
              "Order ID 789: Smartphone Case (Out for Delivery)",
              "Order ID 101: Smartwatch (Refund)",
              "Order ID 202: Bluetooth Speaker (Delivered)"
            ],
            voiceInputs: [],
            voiceProvider: "voicex",
            try_speaking: [
              "I'd like to return the Smartphone Case. How do I send it back and when will the refund show up?"
            ],
            activities: [
              "Track/Cancel order",
              "Change delivery instructions",
              "Exchange and refund status",
              "Return order"
            ],
            phoneNumber: "+1-800-000-0000",
            language: "English",
            default_voiceId: "default",
            voiceConfig: {
              baseUrl: "https://external-proxy-us.nurixlabs.tech",
              user_id: "idn_30B1uTyEU6DGDuC7Oio2RjPFWpE",
              agent_id: "bb9f0993-f38e-49be-aa6f-452b7895e5a3",
              tenant_id: "",
              workspace_id: "8df3c72c-12a4-4b2b-8f6c-c15689e2cb8a",
              overide_previous_context: true
            },
            optional_voices: [
              {
                id: "default",
                name: "Alice",
                icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female2.png",
                description:
                  "An agent that provides real-time order tracking and return process guidance.",
                language: "English",
                region: "USA"
              }
            ]
          }
        ]
      },
      {
        cardTag: "Multi-category",
        cardTitle: "Reward Supports",
        features: [
          { id: 1, text: "Check Points" },
          { id: 2, text: "Redeem Offers" },
          { id: 3, text: "Notify Expiry" }
        ],
        profilePhoto:
          "https://images.unsplash.com/photo-1506744038136-46273834b3fb?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80",
        agentsArray: [
          {
            id: "3b8a248c-01d5-49dd-abf1-c81bfb729aeb",
            name: "Meera",
            description:
              "The agent handles customer queries related to rewards, redemptions, and program eligibility.",
            subDescription:
              "The agent handles customer queries related to rewards, redemptions, and program eligibility.",
            agentName: "Rewards Support",
            icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female1.png",
            chatIcon:
              "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female1.png-chat",
            featureList: [
              "Troubleshoot missing rewards stars",
              "Calculate expected stars based on rewards rules",
              "Initiate star corrections when customer is entitled"
            ],
            voice: "true",
            chat: "false",
            callTitle: "Rewards Support Agent",
            chatHints: [],
            callHintsTable: [],
            callHints: ["Phone Number: 98765 43210"],
            voiceInputs: [],
            voiceProvider: "voicex",
            try_speaking: [
              "मैं अपने Star Rewards को कैसे रिडीम कर सकती हूँ?",
              "मेरे reward points अभी तक credit क्यों नहीं हुए हैं?"
            ],
            activities: [],
            phoneNumber: "+1-800-000-0000",
            language: "Hinglish",
            default_voiceId: "default",
            voiceConfig: {
              baseUrl: VOICEX_URL_CONSTANTS["voicex-in"],
              user_id: "idn_2zXrDB1ZzqaM574PE2IOlSiulmJ",
              agent_id: "3b8a248c-01d5-49dd-abf1-c81bfb729aeb",
              tenant_id: "",
              workspace_id: "93debacb-6338-4fe3-befb-731e7fd94a3b",
              overide_previous_context: true
            },
            optional_voices: [
              {
                id: "default",
                name: "Meera",
                icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female1.png",
                description:
                  "The agent handles customer queries related to rewards, redemptions, and program eligibility.",
                language: "hinglish",
                region: "India"
              }
            ]
          },
          {
            id: "474dcdcd-9a5f-4ebd-9491-b29ee2ffd360",
            name: "Rewards Support",
            description:
              "The agent handles customer queries related to rewards, redemptions, and program eligibility.",
            subDescription:
              "The agent handles customer queries related to rewards, redemptions, and program eligibility.",
            agentName: "Rewards Support Agent",
            icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female1.png",
            chatIcon:
              "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female1.png-chat",
            featureList: [
              "Troubleshoot missing rewards stars",
              "Calculate expected stars based on rewards rules",
              "Initiate star corrections when customer is entitled"
            ],
            voice: "true",
            chat: "false",
            callTitle: "Rewards Support Agent",
            chatHints: [],
            callHintsTable: [],
            callHints: ["Phone Number: 202 555 0123"],
            voiceInputs: [],
            voiceProvider: "voicex",
            try_speaking: [
              "How do I redeem my star rewards?",
              "Why haven't my reward points been credited?",
              "What's my current reward balance?"
            ],
            activities: [],
            phoneNumber: "+1-800-000-0000",
            language: "English",
            default_voiceId: "default",
            voiceConfig: {
              baseUrl: "https://external-proxy-us.nurixlabs.tech",
              user_id: "idn_30B1uTyEU6DGDuC7Oio2RjPFWpE",
              agent_id: "474dcdcd-9a5f-4ebd-9491-b29ee2ffd360",
              tenant_id: "",
              workspace_id: "8df3c72c-12a4-4b2b-8f6c-c15689e2cb8a",
              overide_previous_context: true
            },
            optional_voices: [
              {
                id: "default",
                name: "Rewards Support",
                icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female1.png",
                description:
                  "The agent handles customer queries related to rewards, redemptions, and program eligibility.",
                language: "English",
                region: "USA"
              }
            ]
          }
        ]
      },
      {
        cardTag: "F&B",
        cardTitle: "Order Taking",
        features: [
          { id: 1, text: "Take Orders" },
          { id: 2, text: "Suggest Combos" },
          { id: 3, text: "Confirm ETA" }
        ],
        profilePhoto:
          "https://images.unsplash.com/photo-1519864600265-abb23847ef2c?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80",
        agentsArray: [
          {
            id: "fab68d43-4c06-4312-8d69-750b392f7dcc",
            name: "Myra",
            description:
              "Empathetic voice agent for planning catering menu, placing order, and scheduling delivery",
            subDescription: "Voice: Excited, Patient",
            agentName: "Catering - Order Taking",
            icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female2.png",
            chatIcon:
              "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female2.png",
            featureList: [
              "Empathetic and Patient",
              "Can help plan party menu",
              "Well-versed with Crispy Burgers brand guidelines",
              "Captures cross-sell opportunities"
            ],
            voice: "true",
            chat: "false",
            callTitle: "Big Burgers Catering Service",
            chatHints: [],
            callHintsTable: [],
            callHints: [
              "Agent Name: Myra",
              "Agent Role: Catering Order Taking"
            ],
            voiceInputs: [],
            voiceProvider: "retell",
            try_speaking: [
              "I need catering for a party of 50 people next weekend",
              "Can you help me plan a menu for my office event?",
              "What are your most popular catering packages?"
            ],
            activities: ["Place Order", "Send Payment Email"],
            phoneNumber: "+1-800-000-0000",
            language: "English",
            default_voiceId: "default",
            voiceConfig: {
              baseUrl: "string",
              user_id: "string",
              agent_id: "fab68d43-4c06-4312-8d69-750b392f7dcc",
              tenant_id: "string",
              workspace_id: "string",
              overide_previous_context: true
            },
            optional_voices: [
              {
                id: "default",
                name: "Myra",
                icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female2.png",
                description: "Female Young & Professional",
                language: "English",
                region: ""
              }
            ]
          }
        ]
      },
      {
        cardTag: "Electricals",
        cardTitle: "Repair & Install Scheduling",
        features: [
          { id: 1, text: "Book Service" },
          { id: 2, text: "Assign Tech" },
          { id: 3, text: "Send Reminder" }
        ],
        profilePhoto:
          "https://images.unsplash.com/photo-1465101178521-c1a9136a3c8b?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80",
        agentsArray: [
          {
            id: "7ea05f80-9c8c-48aa-9ff5-c32239e24ac5",
            name: "Nidhi",
            description:
              "A support agent that greets callers and verifies their identity then either logs or escalates the service while owning the case.",
            subDescription:
              "A support agent that greets callers and verifies their identity then either logs or escalates the service while owning the case.",
            agentName: "Customer Support Agent",
            icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female5.png",
            chatIcon:
              "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female5.png-chat",
            featureList: [
              "Helps Nuplay Electricals customers log service or installation requests with warmth, clarity, and zero hold-time.",
              "First tries troubleshooting, checks warranty, and schedules technician visits - all in friendly Hinglish."
            ],
            voice: "true",
            chat: "false",
            callTitle: "Customer Support Agent",
            chatHints: [],
            callHintsTable: [],
            callHints: [],
            voiceInputs: [],
            voiceProvider: "voicex",
            try_speaking: [
              "अगर product warranty में है, तो क्या आप free technician visit schedule कर सकते हैं?",
              "मैंने हाल ही में Nuplay Electricals का fan लिया था, लेकिन वो अभी चल नहीं रहा है।"
            ],
            activities: [
              "Log service requests",
              "Schedule technician visits",
              "Troubleshoot issues"
            ],
            phoneNumber: "+1-800-000-0000",
            language: "Hinglish",
            default_voiceId: "",
            voiceConfig: {
              baseUrl: "https://external-proxy.nurixlabs.tech",
              user_id: "idn_2zXrB6H5u0sCDUJ86YmYj6H7PBW",
              agent_id: "7ea05f80-9c8c-48aa-9ff5-c32239e24ac5",
              tenant_id: "93debacb-6338-4fe3-befb-731e7fd94a3b",
              workspace_id: "93debacb-6338-4fe3-befb-731e7fd94a3b",
              overide_previous_context: true
            },
            optional_voices: [
              {
                id: "default",
                name: "Nidhi",
                icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female5.png",
                description:
                  "A support agent that greets callers and verifies their identity then either logs or escalates the service while owning the case.",
                language: "Hinglish",
                region: ""
              }
            ]
          }
        ]
      },
      {
        cardTag: "Multi-category",
        cardTitle: "Vendor Support Representative",
        features: [
          { id: 1, text: "Onboard Vendor" },
          { id: 2, text: "Update Inventory" },
          { id: 3, text: "Resolve Issues" }
        ],
        profilePhoto:
          "https://images.unsplash.com/photo-1519125323398-675f0ddb6308?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80",
        agentsArray: [
          {
            id: "26db7ac5-5f08-4a12-8c27-4755a4e28661",
            name: "Alex",
            description:
              "A professional and empathetic voice agent that helps resolve vendors' account issues (payments, shipments, compliance docs) quickly and calmly",
            subDescription:
              "A professional and empathetic voice agent that helps resolve vendors' account issues (payments, shipments, compliance docs) quickly and calmly",
            agentName: "Vendor Support Representative",
            icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/male1.png",
            chatIcon:
              "https://d3udl6i7sipqjw.cloudfront.net/agent-images/male1.png-chat",
            featureList: [
              "Handles payment releases, shipment delays, and compliance document uploads with conditional workflows",
              "Validates caller identity before sharing account details, with automatic human handoff after failed attempts"
            ],
            voice: "true",
            chat: "false",
            callTitle: "Vendor Support Representative",
            chatHints: [],
            callHintsTable: [],
            callHints: [
              "Vendor Name: ABC Traders",
              "Vendor ID : ABCT-0912",
              "Last four digits of phone number: 9123"
            ],
            voiceInputs: [],
            voiceProvider: "voicex",
            try_speaking: [
              "Can you review my vendor profile please ?",
              "Hi, I'm calling about my payment for invoice 959 - it hasn't come through yet."
            ],
            activities: [],
            phoneNumber: "+1-800-000-0000",
            language: "English",
            default_voiceId: "default",
            voiceConfig: {
              baseUrl: "https://external-proxy-us.nurixlabs.tech",
              user_id: "idn_310E7A2TSJXdMLERCHCAWmUoo68",
              agent_id: "26db7ac5-5f08-4a12-8c27-4755a4e28661",
              tenant_id: "",
              workspace_id: "8df3c72c-12a4-4b2b-8f6c-c15689e2cb8a",
              overide_previous_context: true
            },
            optional_voices: [
              {
                id: "default",
                name: "Alex",
                icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/male1.png",
                description:
                  "A professional and empathetic voice agent that helps resolve vendors' account issues (payments, shipments, compliance docs) quickly and calmly",
                language: "English",
                region: "US"
              }
            ]
          },
          {
            id: "41146d53-2857-4009-8af7-27f8d0155a48",
            name: "Shweta",
            description:
              "A voice agent that helps resolve vendors' account issues (payments, shipments, compliance docs).",
            subDescription:
              "A voice agent that helps resolve vendors' account issues (payments, shipments, compliance docs).",
            agentName: "Vendor Support Representative",
            icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female5.png",
            chatIcon:
              "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female5.png-chat",
            featureList: [
              "Handles payment releases, shipment delays, and compliance document uploads",
              "Validates caller identity before sharing account details, with automatic human handoff after failed attempts"
            ],
            voice: "true",
            chat: "false",
            callTitle: "Vendor Support",
            chatHints: [],
            callHintsTable: [],
            callHints: [
              "Vendor ID : ABCT-0912",
              "Last four digits of phone number: 9123"
            ],
            voiceInputs: [],
            voiceProvider: "voicex",
            try_speaking: [
              "Can you review my vendor profile please?",
              "Hi, I'm calling about my payment for invoice 959 - it hasn't come through yet."
            ],
            activities: [],
            phoneNumber: "+1-800-000-0000",
            language: "Hinglish",
            default_voiceId: "default",
            voiceConfig: {
              baseUrl: VOICEX_URL_CONSTANTS["voicex-in"],
              user_id: "idn_2zXrDB1ZzqaM574PE2IOlSiulmJ",
              agent_id: "41146d53-2857-4009-8af7-27f8d0155a48",
              tenant_id: "",
              workspace_id: "93debacb-6338-4fe3-befb-731e7fd94a3b",
              overide_previous_context: true
            },
            optional_voices: [
              {
                id: "default",
                name: "Shweta",
                icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female5.png",
                description:
                  "A voice agent that helps resolve vendors' account issues (payments, shipments, compliance docs).",
                language: "Hinglish",
                region: "India"
              }
            ]
          }
        ]
      },
      {
        cardTag: "Multi-Category Retail",
        cardTitle: "CSAT Feedback Agent",
        features: [
          { id: 1, text: "Collects customer satisfaction ratings" },
          { id: 2, text: "Tracks resolution status" },
          { id: 3, text: "Gathers improvement suggestions" }
        ],
        profilePhoto:
          "https://images.unsplash.com/photo-1519125323398-675f0ddb6308?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80",
        agentsArray: [
          {
            id: "24d968cc-0067-4d65-8d1e-2eb2c5281376",
            name: "Sofia",
            description:
              "An agent that collects customer satisfaction ratings, resolution status, and improvement suggestions",
            subDescription:
              "An agent that collects customer satisfaction ratings, resolution status, and improvement suggestions",
            agentName: "CSAT Feedback Agent",
            icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female4.png",
            chatIcon:
              "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female4.png-chat",
            featureList: [
              "Adaptive 3-question survey flow",
              "Opt-out and callback scheduling capabilities",
              "Active listening cues"
            ],
            voice: "true",
            chat: "false",
            callTitle: "CSAT Feedback Agent",
            chatHints: [],
            callHintsTable: [],
            callHints: [],
            voiceInputs: [],
            voiceProvider: "retell",
            try_speaking: [],
            activities: [
              "Collects 1-5 satisfaction rating",
              "Assesses issue resolution status",
              "Gathers qualitative improvement feedback"
            ],
            phoneNumber: "+1-800-000-0000",
            language: "Spanish",
            default_voiceId: "11labs-Zuri",
            voiceConfig: {
              baseUrl: "string",
              user_id: "string",
              agent_id: "ba1e9007-d46a-458f-b90c-def031081c75",
              tenant_id: "string",
              workspace_id: "string",
              overide_previous_context: true
            },
            optional_voices: [
              {
                id: "11labs-Zuri",
                name: "Sofia",
                icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female4.png",
                description: "Female Old & Professional",
                language: "Spanish",
                region: "American"
              }
            ]
          }
        ]
      }
    ],
    financial: [
      {
        cardTag: "Personal Loan Sales",
        cardTitle: "Funnel Re-Engagement",
        features: [
          { id: 1, text: "Reconnect Leads" },
          { id: 2, text: "Share Offers" },
          { id: 3, text: "Update Info" }
        ],
        profilePhoto:
          "https://images.unsplash.com/photo-1519125323398-675f0ddb6308?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80",
        agentsArray: [
          {
            id: "3bf53a47-82b5-4be1-b4a6-7e82ce579378",
            name: "Rahul",
            description:
              "An advanced outbound personal loan agent from Nuplay Credit, assisting users with loan completion process, helping resolve queries etc.",
            subDescription:
              "An advanced outbound personal loan agent from Nuplay Credit, assisting users with loan completion process, helping resolve queries etc.",
            agentName: "Funnel Re-Engagement Agent",
            icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/male2.png",
            chatIcon:
              "https://d3udl6i7sipqjw.cloudfront.net/agent-images/male2.png-chat",
            featureList: [
              "Answers simple FAQs, removes friction, sends a notification link and always asks one question at a time.",
              "Uses soft name confirmation, honors do-not-call requests, and routes complex issues."
            ],
            voice: "true",
            chat: "false",
            callTitle: "Funnel Re-Engagement Agent",
            chatHints: [],
            callHintsTable: [],
            callHints: [],
            voiceInputs: [],
            voiceProvider: "voicex",
            try_speaking: [
              "मुझे interest rate high लग रहा था, क्या थोड़ा कम हो सकता है?",
              "मेरे पास दूसरे app पे better offer है, Nuplay Credit पे क्या ज़्यादा benefit मिलेगा?"
            ],
            activities: [],
            phoneNumber: "+1-800-000-0000",
            language: "Hinglish",
            default_voiceId: "",
            voiceConfig: {
              baseUrl: "https://external-proxy.nurixlabs.tech",
              user_id: "idn_2zXrB6H5u0sCDUJ86YmYj6H7PBW",
              agent_id: "3bf53a47-82b5-4be1-b4a6-7e82ce579378",
              tenant_id: "93debacb-6338-4fe3-befb-731e7fd94a3b",
              workspace_id: "93debacb-6338-4fe3-befb-731e7fd94a3b",
              overide_previous_context: true
            },
            optional_voices: [
              {
                id: "default",
                name: "Rahul",
                icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/male2.png",
                description:
                  "An advanced outbound personal loan agent from Nuplay Credit, assisting users with loan completion process, helping resolve queries etc.",
                language: "Hinglish",
                region: ""
              }
            ]
          },
          {
            id: "c2deed35-f453-4c38-8d92-de823dfa0112",
            name: "Ryan",
            description:
              "An advanced outbound personal loan agent from Nuplay Bank, assisting users with loan completion process and resolving queries.",
            subDescription:
              "An advanced outbound personal loan agent from Nuplay Bank, assisting users with loan completion process and resolving queries.",
            agentName: "Funnel Re-Engagement Agent",
            icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/male1.png",
            chatIcon:
              "https://d3udl6i7sipqjw.cloudfront.net/agent-images/male1.png-chat",
            featureList: [
              "Answers simple FAQs, removes friction, sends a notification link and always asks one question at a time.",
              "Uses soft name confirmation, honors do-not-call requests, and routes complex issues."
            ],
            voice: "true",
            chat: "false",
            callTitle: "Funnel Re-Engagement Agent",
            chatHints: [],
            callHintsTable: [],
            callHints: [],
            voiceInputs: [],
            voiceProvider: "voicex",
            try_speaking: [
              "What are the benefits of Nuplay Bank?",
              "The interest rate seems too high. Can it be lowered?"
            ],
            activities: [],
            phoneNumber: "+1-800-000-0000",
            language: "English",
            default_voiceId: "",
            voiceConfig: {
              baseUrl: "https://external-proxy-us.nurixlabs.tech",
              user_id: "idn_30B1uTyEU6DGDuC7Oio2RjPFWpE",
              agent_id: "c2deed35-f453-4c38-8d92-de823dfa0112",
              tenant_id: "",
              workspace_id: "8df3c72c-12a4-4b2b-8f6c-c15689e2cb8a",
              overide_previous_context: true
            },
            optional_voices: [
              {
                id: "default",
                name: "Ryan",
                icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/male1.png",
                description:
                  "An advanced outbound personal loan agent from Nuplay Bank, assisting users with loan completion process and resolving queries.",
                language: "English",
                region: "USA"
              }
            ]
          }
        ]
      },
      {
        cardTag: "Personal Loan Sales",
        cardTitle: "Lead Qualification",
        features: [
          { id: 1, text: "Screen Leads" },
          { id: 2, text: "Collect Details" },
          { id: 3, text: "Route Prospects" }
        ],
        profilePhoto:
          "https://images.unsplash.com/photo-1506744038136-46273834b3fb?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80",
        agentsArray: [
          {
            id: "8ac2da2e-3778-4762-a037-4e1497b7a997",
            name: "Nisha",
            description:
              "An active listening voice agent that understands user issues and engages with the customer to provide solutions, with a focus on lead generation.",
            subDescription:
              "An active listening voice agent that understands user issues and engages with the customer to provide solutions, with a focus on lead generation.",
            agentName: "Lead Qualification Agent",
            icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female1.png",
            chatIcon:
              "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female1.png-chat",
            featureList: [
              "Tries to analyse the user's point of view",
              "Builds rapport with the customer and convinces them to continue with the discontinued process."
            ],
            voice: "true",
            chat: "false",
            callTitle: "Lead Qualification Agent",
            chatHints: [],
            callHintsTable: [],
            callHints: [
              "Maine bas app download kiya tha explore karne ke liye",
              "Mujhe loan apply karna tha lekin application freeze ho gayi thi"
            ],
            voiceInputs: [],
            voiceProvider: "voicex",
            try_speaking: [
              "Maine bas app download kiya tha explore karne ke liye",
              "Mujhe loan apply karna tha lekin application freeze ho gayi thi"
            ],
            activities: [],
            phoneNumber: "+1-800-000-0000",
            language: "Hinglish",
            default_voiceId: "",
            voiceConfig: {
              baseUrl: "https://external-proxy.nurixlabs.tech",
              user_id: "idn_30B0ObpQWL4E9p2qiU1XyvII9CP",
              agent_id: "8ac2da2e-3778-4762-a037-4e1497b7a997",
              tenant_id: "93debacb-6338-4fe3-befb-731e7fd94a3b",
              workspace_id: "93debacb-6338-4fe3-befb-731e7fd94a3b",
              overide_previous_context: true
            },
            optional_voices: [
              {
                id: "default",
                name: "Nisha",
                icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female1.png",
                description:
                  "An active listening voice agent that understands user issues and engages with the customer to provide solutions, with a focus on lead generation.",
                language: "Hinglish",
                region: ""
              }
            ]
          },
          {
            id: "c8c18f9a-a47f-4b47-8f8b-224ccf398115",
            name: "Emily",
            description:
              "An active listening voice agent that understands user issues and engages with the customer to provide solutions, with a focus on lead generation.",
            subDescription:
              "An active listening voice agent that understands user issues and engages with the customer to provide solutions, with a focus on lead generation.",
            agentName: "Lead Qualification Agent",
            icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female3.png",
            chatIcon:
              "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female3.png-chat",
            featureList: [
              "Tries to analyse the user’s point of view",
              "Builds rapport with the customer and convinces them to continue with the discontinued process"
            ],
            voice: "true",
            chat: "false",
            callTitle: "Lead Qualification Agent",
            chatHints: [],
            callHintsTable: [],
            callHints: ["Name: John Doe", "Email: john.doe@gmail.com"],
            voiceInputs: [],
            voiceProvider: "voicex",
            try_speaking: [
              "I just downloaded the app to explore.",
              "I wanted to apply for a loan but the application froze."
            ],
            activities: [],
            phoneNumber: "+1-800-000-0000",
            language: "English",
            default_voiceId: "",
            voiceConfig: {
              baseUrl: "https://external-proxy-us.nurixlabs.tech",
              user_id: "idn_30B1uTyEU6DGDuC7Oio2RjPFWpE",
              agent_id: "c8c18f9a-a47f-4b47-8f8b-224ccf398115",
              tenant_id: "",
              workspace_id: "8df3c72c-12a4-4b2b-8f6c-c15689e2cb8a",
              overide_previous_context: true
            },
            optional_voices: [
              {
                id: "default",
                name: "Emily",
                icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female3.png",
                description:
                  "An active listening voice agent that understands user issues and engages with the customer to provide solutions, with a focus on lead generation.",
                language: "English",
                region: "USA"
              }
            ]
          }
        ]
      },
      {
        cardTag: "Home Mortgage",
        cardTitle: "Lead Qualification",
        features: [
          { id: 1, text: "Pre-qualify Leads" },
          { id: 2, text: "Capture Property Info" },
          { id: 3, text: "Send for Approval" }
        ],
        profilePhoto:
          "https://images.unsplash.com/photo-1465101178521-c1a9136a3c8b?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80",
        agentsArray: [
          {
            id: "edb6f09b-e566-4ba3-8ea5-61b3c65e8edd",
            name: "Neha",
            description:
              "The agent from Nuplay Mortgage helps user with the lead qualification related to Nuplay mortgage.",
            subDescription:
              "The agent from Nuplay Mortgage helps user with the lead qualification related to Nuplay mortgage.",
            agentName: "Lead Qualification Agent",
            icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female5.png",
            chatIcon:
              "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female5.png-chat",
            featureList: [
              "Verifies if the inquiry is mortgage or loan-related before proceeding",
              "Checks if the user has previously spoken to a Nuplay Mortgage loan officer",
              "Collects key lead qualification details to connect with a home loan specialist"
            ],
            voice: "true",
            chat: "false",
            callTitle: "Lead Qualification Agent",
            chatHints: [],
            callHintsTable: [],
            callHints: [],
            voiceInputs: [],
            voiceProvider: "voicex",
            try_speaking: [],
            activities: [
              "Identifies customer needs (purchase vs refinance)",
              "Identifies qualified vs unqualified leads",
              "Defers complex questions to loan officers appropriately"
            ],
            phoneNumber: "+1-800-000-0000",
            language: "Hinglish",
            default_voiceId: "default",
            voiceConfig: {
              baseUrl: VOICEX_URL_CONSTANTS["voicex-in"],
              user_id: "idn_2zXrDB1ZzqaM574PE2IOlSiulmJ",
              agent_id: "edb6f09b-e566-4ba3-8ea5-61b3c65e8edd",
              tenant_id: "",
              workspace_id: "93debacb-6338-4fe3-befb-731e7fd94a3b",
              overide_previous_context: true
            },
            optional_voices: [
              {
                id: "default",
                name: "Neha",
                icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female5.png",
                description:
                  "The agent from Nuplay Mortgage helps user with the lead qualification related to Nuplay mortgage.",
                language: "Hinglish",
                region: "India"
              }
            ]
          }
        ]
      },
      {
        cardTag: "Account Recovery",
        cardTitle: "Customer Support",
        features: [
          { id: 1, text: "Verify Identity" },
          { id: 2, text: "Reset Access" },
          { id: 3, text: "Escalate Cases" }
        ],
        profilePhoto:
          "https://images.unsplash.com/photo-1560250097-0b93528c311a?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80",
        agentsArray: [
          {
            id: "ba1e9007-d46a-458f-b90c-def031081c75",
            name: "Susan",
            description:
              "A warm, calm, and attentive virtual support representative from Nuplay Tech.",
            subDescription:
              "A warm, calm, and attentive virtual support representative from Nuplay Tech.",
            agentName: "Customer Support",
            icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female3.png",
            chatIcon:
              "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female3.png-chat",
            featureList: [
              "Guides them through Password Reset",
              "Assists with Adding or Updating Bank Accounts",
              "Helping unlock Hard-Locked Accounts"
            ],
            voice: "true",
            chat: "false",
            callTitle: "Customer Support - Automation",
            chatHints: [],
            callHintsTable: [],
            callHints: [
              "Name : John Smith",
              "Username: john.smith92",
              "Email Address: john.smith92@gmail.com",
              "eWallet ID: EW-1234",
              "Date of Birth: March 14, 1992",
              "ZIP Code: 94107"
            ],
            voiceInputs: [],
            voiceProvider: "retell",
            try_speaking: [
              "I forgot my password and can't log into my account",
              "I need help updating my bank account information",
              "My account is locked and I can't access it"
            ],
            activities: [
              "Password reset assistance",
              "Bank account updates",
              "Account unlock support"
            ],
            phoneNumber: "+1-800-000-0000",
            language: "English",
            default_voiceId: "",
            voiceConfig: {
              baseUrl: "string",
              user_id: "string",
              agent_id: "ba1e9007-d46a-458f-b90c-def031081c75",
              tenant_id: "string",
              workspace_id: "string",
              overide_previous_context: true
            },
            optional_voices: [
              {
                id: "11labs-Zuri",
                name: "Susan",
                icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female3.png",
                description: "Female Old & Professional",
                language: "English",
                region: "American"
              }
            ]
          }
        ]
      },
      {
        cardTag: "Recovery",
        cardTitle: "EMI Reminder",
        features: [
          { id: 1, text: "Send Reminders" },
          { id: 2, text: "Share Pay Link" },
          { id: 3, text: "Notify Late Fee" }
        ],
        profilePhoto:
          "https://images.unsplash.com/photo-1560250097-0b93528c311a?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80",
        agentsArray: [
          {
            id: "65a03fb0-c85a-45e8-ab42-1a50b4977e41",
            name: "Raj",
            description:
              "A calm and attentive outbound agent who listens actively and gently reminds customers about payment while maintaining a composed and professional conduct.",
            subDescription:
              "A calm and attentive outbound agent who listens actively and gently reminds customers about payment while maintaining a composed and professional conduct.",
            agentName: "Debt Recovery Agent",
            icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/male2.png",
            chatIcon:
              "https://d3udl6i7sipqjw.cloudfront.net/agent-images/male2.png",
            featureList: [
              "Give appropriate reason for not giving the payment",
              "Identify the user's requirements clearly and accurately."
            ],
            voice: "true",
            chat: "false",
            callTitle: "Debt Recovery Agent",
            chatHints: [],
            callHintsTable: [],
            callHints: [],
            voiceInputs: [],
            voiceProvider: "voicex",
            try_speaking: [
              "Mujhe bilkul payment nahi karni.",
              "Mujhe yaad nahi ki Maine credit kabh liya tha"
            ],
            activities: [
              "Payment reminder calls",
              "Debt settlement negotiations",
              "Customer requirement assessment"
            ],
            phoneNumber: "+1-800-000-0000",
            language: "Hinglish",
            default_voiceId: "default",
            voiceConfig: {
              baseUrl: "https://external-proxy.nurixlabs.tech",
              user_id: "40d323bc-dc17-4b3d-a7ac-8ded8a0b16ff",
              agent_id: "65a03fb0-c85a-45e8-ab42-1a50b4977e41",
              tenant_id: "93debacb-6338-4fe3-befb-731e7fd94a3b",
              workspace_id: "93debacb-6338-4fe3-befb-731e7fd94a3b",
              overide_previous_context: true
            },
            optional_voices: [
              {
                id: "default",
                name: "Raj",
                icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/male2.png",
                description: "Male Calm & Professional",
                language: "Hinglish",
                region: ""
              }
            ]
          },
          {
            id: "23c93602-366c-44cb-ba2d-c4a81c0cde8f",
            name: "Michael",
            description:
              "An agent who listens actively and gently reminds customers about payment while maintaining a composed and professional conduct.",
            subDescription:
              "An agent who listens actively and gently reminds customers about payment while maintaining a composed and professional conduct.",
            agentName: "EMI Reminder Agent",
            icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/male1.png",
            chatIcon:
              "https://d3udl6i7sipqjw.cloudfront.net/agent-images/male1.png-chat",
            featureList: [
              "Figures out reason for non-payment.",
              "Identifies the user's requirements clearly and accurately."
            ],
            voice: "true",
            chat: "false",
            callTitle: "EMI Reminder Agent",
            chatHints: [],
            callHintsTable: [],
            callHints: [],
            voiceInputs: [],
            voiceProvider: "voicex",
            try_speaking: ["I do not want to pay right now."],
            activities: [],
            phoneNumber: "+1-800-000-0000",
            language: "English",
            default_voiceId: "",
            voiceConfig: {
              baseUrl: "https://external-proxy-us.nurixlabs.tech",
              user_id: "idn_30B1uTyEU6DGDuC7Oio2RjPFWpE",
              agent_id: "23c93602-366c-44cb-ba2d-c4a81c0cde8f",
              tenant_id: "",
              workspace_id: "8df3c72c-12a4-4b2b-8f6c-c15689e2cb8a",
              overide_previous_context: true
            },
            optional_voices: [
              {
                id: "default",
                name: "Michael",
                icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/male1.png",
                description:
                  "An agent who listens actively and gently reminds customers about payment while maintaining a composed and professional conduct.",
                language: "English",
                region: "USA"
              }
            ]
          }
        ]
      },
      {
        cardTag: "Recovery",
        cardTitle: "Collections",
        features: [
          { id: 1, text: "Follow Up" },
          { id: 2, text: "Offer Plans" },
          { id: 3, text: "Confirm Payment" }
        ],
        profilePhoto:
          "https://images.unsplash.com/photo-1560250097-0b93528c311a?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80",
        agentsArray: [
          {
            id: "0d3bd471-3c7c-4da3-8cef-54cae5d7a10c",
            name: "Maria",
            description:
              "An agent that guides overdue customers through identity verification, payment options, and consequences",
            subDescription:
              "An agent that guides overdue customers through identity verification, payment options, and consequences",
            agentName: "Debt Collection Agent",
            icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female3.png",
            chatIcon:
              "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female3.png-chat",
            featureList: [
              "Optimal Debt Resolution Strategies",
              "Reminds User to Pay Overdue"
            ],
            voice: "true",
            chat: "false",
            callTitle: "Debt Collection Agent",
            chatHints: [],
            callHintsTable: [],
            callHints: [
              "Name: Mr Robin Smith",
              "Social Security Code: 3456",
              "Date of Birth: 2nd Jan 1998"
            ],
            voiceInputs: [],
            voiceProvider: "voicex",
            try_speaking: [
              "I cannot share my Social Security Number.",
              "I can't pay in 10 months, could you please share another plan?"
            ],
            activities: [],
            phoneNumber: "+1-800-000-0000",
            language: "English",
            default_voiceId: "",
            voiceConfig: {
              baseUrl: "https://external-proxy-us.nurixlabs.tech",
              user_id: "idn_30B1uTyEU6DGDuC7Oio2RjPFWpE",
              agent_id: "0d3bd471-3c7c-4da3-8cef-54cae5d7a10c",
              tenant_id: "",
              workspace_id: "8df3c72c-12a4-4b2b-8f6c-c15689e2cb8a",
              overide_previous_context: true
            },
            optional_voices: [
              {
                id: "default",
                name: "Maria",
                icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female3.png",
                description:
                  "An agent that guides overdue customers through identity verification, payment options, and consequences",
                language: "English",
                region: "USA"
              }
            ]
          },
          {
            id: "f408ab5b-ea26-42ec-b311-f86f4ff5e9ec",
            name: "Raj",
            description: "Remind Users to Pay Overdue",
            subDescription: "Remind Users to Pay Overdue",
            agentName: "Debt Collection Agent",
            icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/male2.png",
            chatIcon:
              "https://d3udl6i7sipqjw.cloudfront.net/agent-images/male2.png",
            featureList: [
              "Optimal Debt Resolution Strategies",
              "Reminds User to Pay Overdue"
            ],
            voice: "true",
            chat: "false",
            callTitle: "Debt Collection Agent",
            chatHints: [],
            callHintsTable: [],
            callHints: [],
            voiceInputs: [],
            voiceProvider: "voicex",
            try_speaking: [
              "I cannot pay you right now.",
              "kya aap mujhe call kar sakte ho?"
            ],
            activities: [],
            phoneNumber: "+1-800-000-0000",
            language: "Hinglish",
            default_voiceId: "default",
            voiceConfig: {
              baseUrl: "https://external-proxy.nurixlabs.tech",
              user_id: "40d323bc-dc17-4b3d-a7ac-8ded8a0b16ff",
              agent_id: "f408ab5b-ea26-42ec-b311-f86f4ff5e9ec",
              tenant_id: "93debacb-6338-4fe3-befb-731e7fd94a3b",
              workspace_id: "93debacb-6338-4fe3-befb-731e7fd94a3b",
              overide_previous_context: true
            },
            optional_voices: [
              {
                id: "default",
                name: "Raj",
                icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/male2.png",
                description: "Male Calm & Assertive",
                language: "Hinglish",
                region: ""
              }
            ]
          }
        ]
      }
    ],
    education: [
      {
        cardTag: "Courses",
        cardTitle: "Lead Qualification",
        features: [
          { id: 1, text: "Screen Leads" },
          { id: 2, text: "Match Courses" },
          { id: 3, text: "Share Next Steps" }
        ],
        profilePhoto:
          "https://images.unsplash.com/photo-1465101046530-73398c7f28ca?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80",
        agentsArray: [
          {
            id: "75a439c8-c899-4de9-916f-9e6b41ca72e8",
            name: "Mira",
            description:
              "An intelligent agent that handles customer interactions, processes requests, and provides automated assistance with PREPCA.",
            subDescription:
              "An intelligent agent that handles customer interactions, processes requests, and provides automated assistance with PREPCA.",
            agentName: "Counselling Agent",
            icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female4.png",
            chatIcon:
              "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female4.png-chat",
            featureList: [
              "Helps students to schedule a free one-on-one mentorship session with top Chartered Accountants through PREPCA.",
              "Offers personalised guidance to students preparing for the CA Exam."
            ],
            voice: "true",
            chat: "false",
            callTitle: "Counselling Agent",
            chatHints: [],
            callHintsTable: [],
            callHints: [],
            voiceInputs: [],
            voiceProvider: "voicex",
            try_speaking: [
              "This is my final attempt at the CA exam. How can PREPCA help ensure I pass this time?",
              "क्या मेरे father इस one-to-one session को join कर सकते हैं?"
            ],
            activities: [
              "Schedule mentorship sessions",
              "Provide CA exam guidance",
              "Connect students with Chartered Accountants"
            ],
            phoneNumber: "+1-800-000-0000",
            language: "Hinglish",
            default_voiceId: "",
            voiceConfig: {
              baseUrl: "https://external-proxy.nurixlabs.tech",
              user_id: "idn_2zaDX6upkyO8xPpdizMLTOMI2vA",
              agent_id: "75a439c8-c899-4de9-916f-9e6b41ca72e8",
              tenant_id: "93debacb-6338-4fe3-befb-731e7fd94a3b",
              workspace_id: "93debacb-6338-4fe3-befb-731e7fd94a3b",
              overide_previous_context: true
            },
            optional_voices: [
              {
                id: "default",
                name: "Mira",
                icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female4.png",
                description:
                  "An intelligent agent that handles customer interactions, processes requests, and provides automated assistance with PREPCA.",
                language: "Hinglish",
                region: "India"
              }
            ]
          }
        ]
      },
      {
        cardTag: "Admissions",
        cardTitle: "Admissions",
        features: [
          { id: 1, text: "Explain Criteria" },
          { id: 2, text: "Track Status" },
          { id: 3, text: "Confirm Admission" }
        ],
        profilePhoto:
          "https://images.unsplash.com/photo-1519864600265-abb23847ef2c?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80",
        agentsArray: [
          {
            id: "801dd116-e70e-4cbf-8b78-e4afe2b44e7b",
            name: "Mira",
            description:
              "Guides students through M.Sc. Data Science programs offered by various universities across India.",
            subDescription:
              "Specialist in M.Sc. Data Science program counseling and curriculum guidance",
            agentName: "Counselling Agent",
            icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female3.png",
            chatIcon:
              "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female3.png-chat",
            featureList: [
              "Detailes about M.Sc. Data Science programs offered by top universities across India",
              "Offers personalised guidance on course curriculum"
            ],
            voice: "true",
            chat: "false",
            callTitle: "Counselling Agent",
            chatHints: [],
            callHintsTable: [],
            callHints: [],
            voiceInputs: [],
            voiceProvider: "voicex",
            try_speaking: [
              "Which university offers the best placement support?",
              "मेरा coding background नहीं है, क्या मैं Msc Data Science के लिए eligible हूँ?"
            ],
            activities: [
              "Provide university program details",
              "Offer curriculum guidance",
              "Assist with eligibility assessment"
            ],
            phoneNumber: "+1-800-000-0000",
            language: "Hinglish",
            default_voiceId: "",
            voiceConfig: {
              baseUrl: "https://external-proxy.nurixlabs.tech",
              user_id: "idn_2zaDX6upkyO8xPpdizMLTOMI2vA",
              agent_id: "801dd116-e70e-4cbf-8b78-e4afe2b44e7b",
              tenant_id: "93debacb-6338-4fe3-befb-731e7fd94a3b",
              workspace_id: "93debacb-6338-4fe3-befb-731e7fd94a3b",
              overide_previous_context: true
            },
            optional_voices: [
              {
                id: "default",
                name: "Mira",
                icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female3.png",
                description:
                  "Guides students through M.Sc. Data Science programs offered by various universities across India.",
                language: "Hinglish",
                region: "India"
              }
            ]
          },
          {
            id: "435fad52-cb2f-4689-9351-c66950985a2b",
            name: "Sarah",
            description:
              "An agent designed to handle common queries related to admissions, academic programs, tuition, extracurricular activities, and campus facilities for an international school.",
            subDescription:
              "An agent designed to handle common queries related to admissions, academic programs, tuition, extracurricular activities, and campus facilities for an international school.",
            agentName: "FAQ Agent",
            icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female4.png",
            chatIcon:
              "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female4.png-chat",
            featureList: [
              "Collects info about parent's grade level preferences and educational priorities",
              "Confirms availability for campus visit and scheduling preferences",
              "Gives information about both schools in Austin, Texas."
            ],
            voice: "true",
            chat: "false",
            callTitle: "FAQ Agent",
            chatHints: [],
            callHintsTable: [],
            callHints: [],
            voiceInputs: [],
            voiceProvider: "voicex",
            try_speaking: [
              "How do I enroll my child?",
              "Do you help students with counselling?"
            ],
            activities: [],
            phoneNumber: "+1-800-000-0000",
            language: "English",
            default_voiceId: "default",
            voiceConfig: {
              baseUrl: "https://external-proxy-us.nurixlabs.tech",
              user_id: "idn_30B1uTyEU6DGDuC7Oio2RjPFWpE",
              agent_id: "435fad52-cb2f-4689-9351-c66950985a2b",
              tenant_id: "",
              workspace_id: "8df3c72c-12a4-4b2b-8f6c-c15689e2cb8a",
              overide_previous_context: true
            },
            optional_voices: [
              {
                id: "default",
                name: "Sarah",
                icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female4.png",
                description:
                  "An agent designed to handle common queries related to admissions, academic programs, tuition, extracurricular activities, and campus facilities for an international school.",
                language: "English",
                region: "USA"
              }
            ]
          }
        ]
      },
      {
        cardTag: "Courses",
        cardTitle: "FAQs",
        features: [
          { id: 1, text: "Course Info" },
          { id: 2, text: "Schedules" },
          { id: 3, text: "Enrollment Help" }
        ],
        profilePhoto:
          "https://images.unsplash.com/photo-1506744038136-46273834b3fb?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80",
        agentsArray: [
          {
            id: "073a02d2-43e0-47cf-ad7d-c8279c549199",
            name: "Sarah",
            description:
              "A voice agent designed to handle common queries related to admissions, academic programs, tuition, extracurricular activities, and campus facilities for an international school",
            subDescription:
              "A voice agent designed to handle common queries related to admissions, academic programs, tuition, extracurricular activities, and campus facilities for an international school",
            agentName: "FAQs Agent",
            icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female4.png",
            chatIcon:
              "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female4.png",
            featureList: [
              "FAQ agent",
              "Appointment Scheduling for school visit"
            ],
            voice: "true",
            chat: "false",
            callTitle: "FAQs Agent",
            chatHints: [],
            callHintsTable: [],
            callHints: [],
            voiceInputs: [],
            voiceProvider: "retell",
            try_speaking: [
              "How do I enroll my child?",
              "What is the School Uniform policy?"
            ],
            activities: [
              "Answer admission FAQs",
              "Schedule school visits",
              "Provide campus information"
            ],
            phoneNumber: "+1-800-000-0000",
            language: "English",
            default_voiceId: "default",
            voiceConfig: {
              baseUrl: "string",
              user_id: "string",
              agent_id: "073a02d2-43e0-47cf-ad7d-c8279c549199",
              tenant_id: "string",
              workspace_id: "string",
              overide_previous_context: true
            },
            optional_voices: [
              {
                id: "default",
                name: "Sarah",
                icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female4.png",
                description: "Female Young & Professional",
                language: "English",
                region: ""
              }
            ]
          }
        ]
      }
    ],
    realestate: [
      // {
      //   cardTag: "Homes",
      //   cardTitle: "Lead Qualification",
      //   features: [
      //     { id: 1, text: "Screen Buyers" },
      //     { id: 2, text: "Capture Needs" },
      //     { id: 3, text: "Schedule Visit" }
      //   ],
      //   profilePhoto:
      //     "https://images.unsplash.com/photo-1582750433449-648ed127bb54?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80",
      //   agentsArray: []
      // }
    ],
    health: [
      {
        cardTag: "Multi-speciality Clinic",
        cardTitle: "Appointment Booking",
        features: [
          { id: 1, text: "Book Slots" },
          { id: 2, text: "Confirm Visit" },
          { id: 3, text: "Send Reminder" }
        ],
        profilePhoto:
          "https://images.unsplash.com/photo-1465101046530-73398c7f28ca?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80",
        agentsArray: [
          {
            id: "45ab1864-96de-4c75-829f-0069610dd62b",
            name: "Shweta",
            description:
              "An agent that handles appointment booking and rescheduling in real time with the ability to confirm, cancel, and modify bookings.",
            subDescription:
              "An agent that handles appointment booking and rescheduling in real time with the ability to confirm, cancel, and modify bookings.",
            agentName: "Appointment Booking Agent",
            icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female5.png",
            chatIcon:
              "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female5.png-chat",
            featureList: [
              "Validates caller identity through multiple security checkpoints before accessing sensitive information",
              "Detects intent and routes to appropriate workflows (booking, rescheduling, cancellation) with policy enforcement"
            ],
            voice: "true",
            chat: "false",
            callTitle: "Appointment Booking",
            chatHints: [],
            callHintsTable: [],
            callHints: [
              "Patient Name: Rajesh Kumar",
              "Customer ID: 123456",
              "DOB: 2nd January, 1998",
              "OTP: 7890"
            ],
            voiceInputs: [],
            voiceProvider: "voicex",
            try_speaking: [
              "Hi, I'd like to book an appointment with a cardiologist.",
              "I need to reschedule my appointment for tomorrow please, something came up."
            ],
            activities: [],
            phoneNumber: "+1-800-000-0000",
            language: "Hinglish",
            default_voiceId: "default",
            voiceConfig: {
              baseUrl: VOICEX_URL_CONSTANTS["voicex-in"],
              user_id: "idn_2zXrDB1ZzqaM574PE2IOlSiulmJ",
              agent_id: "45ab1864-96de-4c75-829f-0069610dd62b",
              tenant_id: "",
              workspace_id: "93debacb-6338-4fe3-befb-731e7fd94a3b",
              overide_previous_context: true
            },
            optional_voices: [
              {
                id: "default",
                name: "Shweta",
                icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female5.png",
                description:
                  "An agent that handles appointment booking and rescheduling in real time with the ability to confirm, cancel, and modify bookings.",
                language: "Hinglish",
                region: "India"
              }
            ]
          },
          {
            id: "f2d67980-31c0-4acd-84e7-20dc80cfb231",
            name: "Maria",
            description:
              "A professional and empathetic agent that handles appointment booking and rescheduling in real time with the ability to confirm, cancel, and modify bookings.",
            subDescription:
              "A professional and empathetic agent that handles appointment booking and rescheduling in real time with the ability to confirm, cancel, and modify bookings.",
            agentName: "Appointment Booking & Support Agent",
            icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female2.png",
            chatIcon:
              "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female2.png-chat",
            featureList: [
              "Validates caller identity through multiple security checkpoints before accessing sensitive information",
              "Detects intent and routes to appropriate workflows (booking, rescheduling, cancellation) with policy enforcement"
            ],
            voice: "true",
            chat: "false",
            callTitle: "Appointment Booking & Support Agent",
            chatHints: [],
            callHintsTable: [],
            callHints: [
              "Patient_Name : Mr Robin Smith",
              "Customer ID : 123456",
              "DOB : 2nd January, 1998",
              "OTP : 7890"
            ],
            voiceInputs: [],
            voiceProvider: "voicex",
            try_speaking: [
              "Hi, I'd like to book an appointment with a cardiologist.",
              "I need to reschedule my appointment for tomorrow please, something came up."
            ],
            activities: [],
            phoneNumber: "+1-800-000-0000",
            language: "English",
            default_voiceId: "default",
            voiceConfig: {
              baseUrl: "https://external-proxy-us.nurixlabs.tech",
              user_id: "idn_310E7A2TSJXdMLERCHCAWmUoo68",
              agent_id: "f2d67980-31c0-4acd-84e7-20dc80cfb231",
              tenant_id: "",
              workspace_id: "8df3c72c-12a4-4b2b-8f6c-c15689e2cb8a",
              overide_previous_context: true
            },
            optional_voices: [
              {
                id: "default",
                name: "Maria",
                icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female2.png",
                description:
                  "A professional and empathetic agent that handles appointment booking and rescheduling in real time with the ability to confirm, cancel, and modify bookings.",
                language: "English",
                region: "US"
              }
            ]
          }
        ]
      },
      {
        cardTag: "Fitness Centers",
        cardTitle: "FAQ",
        features: [
          { id: 1, text: "Membership Info" },
          { id: 2, text: "Class Schedules" },
          { id: 3, text: "Trainer Details" }
        ],
        profilePhoto:
          "https://images.unsplash.com/photo-1519864600265-abb23847ef2c?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80",
        agentsArray: [
          {
            id: "17fe1f4c-3ffd-4132-8879-97b1a15c6940",
            name: "Riya",
            description:
              "The agent from Nuplay Fitness helps with membership queries and plan changes, freezes or cancellations.",
            subDescription:
              "The agent from Nuplay Fitness helps with membership queries and plan changes, freezes or cancellations.",
            agentName: "FAQ Agent",
            icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female5.png",
            chatIcon:
              "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female5.png-chat",
            featureList: [
              "Tracks membership type, billing dates, and freeze window",
              "Compares different membership options and suggests best-fit plans",
              "Handles joining, access changes, freezes, and cancellations"
            ],
            voice: "true",
            chat: "false",
            callTitle: "FAQ Agent",
            chatHints: [],
            callHintsTable: [],
            callHints: ["Name: Arjun", "Phone Number: 98765 43210"],
            voiceInputs: [],
            voiceProvider: "voicex",
            try_speaking: [
              "आपके पास कौन-कौन से membership options available हैं?",
              "अगर मुझे अपना account temporarily pause करना हो, तो वो कैसे करूँ?",
              "क्या मैं अपनी membership cancel कर सकता हूँ?"
            ],
            activities: [],
            phoneNumber: "+1-800-000-0000",
            language: "Hinglish",
            default_voiceId: "default",
            voiceConfig: {
              baseUrl: VOICEX_URL_CONSTANTS["voicex-in"],
              user_id: "idn_2zXrDB1ZzqaM574PE2IOlSiulmJ",
              agent_id: "17fe1f4c-3ffd-4132-8879-97b1a15c6940",
              tenant_id: "",
              workspace_id: "93debacb-6338-4fe3-befb-731e7fd94a3b",
              overide_previous_context: true
            },
            optional_voices: [
              {
                id: "default",
                name: "Riya",
                icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female5.png",
                description:
                  "The agent from Nuplay Fitness helps with membership queries and plan changes, freezes or cancellations.",
                language: "Hinglish",
                region: "India"
              }
            ]
          },
          {
            id: "b3dbb609-80f9-49fe-bb13-3b3bd8e4291f",
            name: "Lina",
            description:
              "The agent from Nuplay Fitness helps with membership queries and plan changes, freezes or cancellations.",
            subDescription:
              "The agent from Nuplay Fitness helps with membership queries and plan changes, freezes or cancellations.",
            agentName: "FAQ Agent",
            icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female3.png",
            chatIcon:
              "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female3.png-chat",
            featureList: [
              "Tracks membership type, billing dates, and freeze window",
              "Compares different membership options and suggests best-fit plans",
              "Handles joining, access changes, freezes, and cancellations"
            ],
            voice: "true",
            chat: "false",
            callTitle: "FAQ Agent",
            chatHints: [],
            callHintsTable: [],
            callHints: ["Name: Jordan Smith", "Phone Number: 123 456 7890"],
            voiceInputs: [],
            voiceProvider: "voicex",
            try_speaking: [
              "What are the different membership options?",
              "How can I freeze my account?",
              "Can I cancel my membership?"
            ],
            activities: [],
            phoneNumber: "+1-800-000-0000",
            language: "English",
            default_voiceId: "default",
            voiceConfig: {
              baseUrl: "https://external-proxy-us.nurixlabs.tech",
              user_id: "idn_30B1uTyEU6DGDuC7Oio2RjPFWpE",
              agent_id: "b3dbb609-80f9-49fe-bb13-3b3bd8e4291f",
              tenant_id: "",
              workspace_id: "8df3c72c-12a4-4b2b-8f6c-c15689e2cb8a",
              overide_previous_context: true
            },
            optional_voices: [
              {
                id: "default",
                name: "Lina",
                icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female3.png",
                description:
                  "The agent from Nuplay Fitness helps with membership queries and plan changes, freezes or cancellations.",
                language: "English",
                region: "USA"
              }
            ]
          }
        ]
      },
      {
        cardTag: "Fitness Centers",
        cardTitle: "Lead Qualification",
        features: [
          { id: 1, text: "Screen Leads" },
          { id: 2, text: "Capture Goals" },
          { id: 3, text: "Share Offers" }
        ],
        profilePhoto:
          "https://images.unsplash.com/photo-1506744038136-46273834b3fb?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80",
        agentsArray: [
          {
            id: "f4d08a72-3eb0-433d-a00a-6dee6737e46e",
            name: "Lead Qualification",
            description:
              "The agent from Nuplay Fitness qualifies leads who visited the website and offers plans.",
            subDescription:
              "The agent from Nuplay Fitness qualifies leads who visited the website and offers plans.",
            agentName: "Lead Qualification Agent",
            icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/male2.png",
            chatIcon:
              "https://d3udl6i7sipqjw.cloudfront.net/agent-images/male2.png-chat",
            featureList: [
              "Gathers fitness goals, timeline, and workout modality preferences",
              "Provides benefits of different options for group vs personal training",
              "Asks purpose, timeline, past plans, preferences and payment method"
            ],
            voice: "true",
            chat: "false",
            callTitle: "Lead Qualification Agent",
            chatHints: [],
            callHintsTable: [],
            callHints: [],
            voiceInputs: [],
            voiceProvider: "voicex",
            try_speaking: [],
            activities: [],
            phoneNumber: "+1-800-000-0000",
            language: "Hinglish",
            default_voiceId: "default",
            voiceConfig: {
              baseUrl: VOICEX_URL_CONSTANTS["voicex-in"],
              user_id: "idn_2zXrDB1ZzqaM574PE2IOlSiulmJ",
              agent_id: "f4d08a72-3eb0-433d-a00a-6dee6737e46e",
              tenant_id: "",
              workspace_id: "93debacb-6338-4fe3-befb-731e7fd94a3b",
              overide_previous_context: true
            },
            optional_voices: [
              {
                id: "default",
                name: "Lead Qualification",
                icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/male2.png",
                description:
                  "The agent from Nuplay Fitness qualifies leads who visited the website and offers plans.",
                language: "Hinglish",
                region: "India"
              }
            ]
          },
          {
            id: "87c86626-e10c-4cbd-b281-839eaa940edb",
            name: "Alex",
            description:
              "The agent from Nuplay Fitness qualifies leads who visited the website and offers plans.",
            subDescription:
              "The agent from Nuplay Fitness qualifies leads who visited the website and offers plans.",
            agentName: "Lead Qualification Agent",
            icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/male1.png",
            chatIcon:
              "https://d3udl6i7sipqjw.cloudfront.net/agent-images/male1.png-chat",
            featureList: [
              "Gathers fitness goals, timeline, and workout modality preferences",
              "Provides benefits of different options for group vs personal training",
              "Asks purpose, timeline, past plans, preferences and payment method"
            ],
            voice: "true",
            chat: "false",
            callTitle: "Lead Qualification Agent",
            chatHints: [],
            callHintsTable: [],
            callHints: [],
            voiceInputs: [],
            voiceProvider: "voicex",
            try_speaking: [
              "Hey, I'm busy right now. Can I call you back later?",
              "I've tried gym memberships before but never stuck with it. Can you help?"
            ],
            activities: [],
            phoneNumber: "+1-800-000-0000",
            language: "English",
            default_voiceId: "default",
            voiceConfig: {
              baseUrl: "https://external-proxy-us.nurixlabs.tech",
              user_id: "idn_30B1uTyEU6DGDuC7Oio2RjPFWpE",
              agent_id: "87c86626-e10c-4cbd-b281-839eaa940edb",
              tenant_id: "",
              workspace_id: "8df3c72c-12a4-4b2b-8f6c-c15689e2cb8a",
              overide_previous_context: true
            },
            optional_voices: [
              {
                id: "default",
                name: "Alex",
                icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/male1.png",
                description:
                  "The agent from Nuplay Fitness qualifies leads who visited the website and offers plans.",
                language: "English",
                region: "USA"
              }
            ]
          }
        ]
      },
      {
        cardTag: "Multi-speciality Clinic",
        cardTitle: "Reschedule Appointment",
        features: [
          { id: 1, text: "Cancel Visit" },
          { id: 2, text: "Reschedule Slot" },
          { id: 3, text: "Confirm Change" }
        ],
        profilePhoto:
          "https://images.unsplash.com/photo-1560250097-0b93528c311a?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80",
        agentsArray: [
          {
            id: "9dc52f79-49ee-4d0a-bfb0-3985825bdcc5",
            name: "Cathy",
            description:
              "Nuplay Hospital is a public hospital in San Francisco, USA. We treat all types of medical conditions. We have world-class doctors from all domains.",
            subDescription:
              "Nuplay Hospital is a public hospital in San Francisco, USA. We treat all types of medical conditions. We have world-class doctors from all domains.",
            agentName: "Cancel / Reschedule an appointment for checkup",
            icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female2.png",
            chatIcon:
              "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female2.png-chat",
            featureList: [
              "Provides details about any checkup",
              "Can cancel any appointment",
              "Helps in rescheduling an appointment"
            ],
            voice: "true",
            chat: "false",
            callTitle: "Cancel / Reschedule an appointment for checkup",
            chatHints: [],
            callHintsTable: [],
            callHints: [
              "Patient name: David Wang",
              "Date of birth: January 3rd, 1997",
              "Email: david@gmail.com",
              "Doctor name: Dr. Charles Smith",
              "Previous Dates Visited: 14/05/2020, 15/06/2023"
            ],
            voiceInputs: [],
            voiceProvider: "retell",
            try_speaking: [
              "I need to cancel my appointment with Dr. Charles Smith next week",
              "Can I reschedule my checkup to a different time?",
              "What are the available slots for my rescheduled appointment?"
            ],
            activities: [
              "Cancel medical appointments",
              "Reschedule patient checkups",
              "Provide appointment details"
            ],
            phoneNumber: "+1-800-000-0000",
            language: "English",
            default_voiceId: "",
            voiceConfig: {
              baseUrl: "string",
              user_id: "string",
              agent_id: "9dc52f79-49ee-4d0a-bfb0-3985825bdcc5",
              tenant_id: "string",
              workspace_id: "string",
              overide_previous_context: true
            },
            optional_voices: [
              {
                id: "11labs-Amy",
                name: "Cathy",
                icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/Female2.png",
                description: "Female Young & Professional",
                language: "English",
                region: "British"
              }
            ]
          }
        ]
      }
    ],
    home: [
      {
        cardTag: "HVAC",
        cardTitle: "Appointment Scheduling",
        features: [
          { id: 1, text: "Book Service" },
          { id: 2, text: "Assign Tech" },
          { id: 3, text: "Confirm Visit" }
        ],
        profilePhoto:
          "https://images.unsplash.com/photo-1582750433449-648ed127bb54?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80",
        agentsArray: [
          {
            id: "fab5ff9c-e095-400a-8011-88586c8e3be5",
            name: "Andrew",
            description:
              "A Customer Support voice agent that assists with HVAC, plumbing, and electrical service inquiries. It helps customers understand service offerings, schedule technician visits, and handle emergencies quickly",
            subDescription:
              "A Customer Support voice agent that assists with HVAC, plumbing, and electrical service inquiries. It helps customers understand service offerings, schedule technician visits, and handle emergencies quickly",
            agentName: "Customer Support Agent",
            icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/male1.png",
            chatIcon:
              "https://d3udl6i7sipqjw.cloudfront.net/agent-images/male1.png",
            featureList: [
              "Book Technician Appointments",
              "Explain Service Coverage & Estimates",
              "Handle Emergency Repair Requests"
            ],
            voice: "true",
            chat: "false",
            callTitle: "Support Agent",
            chatHints: [],
            callHintsTable: [],
            callHints: [],
            voiceInputs: [],
            voiceProvider: "retell",
            try_speaking: [
              "Can I book a plumber for tomorrow morning?",
              "My AC isn't cooling — can someone check it today ?",
              "What's your service charge for electrical work?"
            ],
            activities: [
              "Schedule & Manage Technician Appointments",
              "Provide Service Details & Pricing Info",
              "Prioritize Emergency Service Requests"
            ],
            phoneNumber: "+1-800-000-0000",
            language: "English",
            default_voiceId: "default",
            voiceConfig: {
              baseUrl: "string",
              user_id: "string",
              agent_id: "fab5ff9c-e095-400a-8011-88586c8e3be5",
              tenant_id: "string",
              workspace_id: "string",
              overide_previous_context: true
            },
            optional_voices: [
              {
                id: "default",
                name: "Andrew",
                icon: "https://d3udl6i7sipqjw.cloudfront.net/agent-images/male1.png",
                description: "Male Young & Professional",
                language: "English",
                region: ""
              }
            ]
          }
        ]
      }
    ]
  };

  // Get all cards if "All" is selected, otherwise get specific category
  const getAllCards = (): CardData[] => {
    const allCards: CardData[] = [];
    Object.keys(cardData).forEach((category: string) => {
      allCards.push(...cardData[category]);
    });
    return allCards;
  };

  const getCardsForCategory = (): CardData[] => {
    if (selectedCategory === 0) {
      return getAllCards();
    }

    const categoryKey = categories.find(
      (cat) => cat.id === selectedCategory
    )?.key;
    return categoryKey ? cardData[categoryKey] || [] : [];
  };

  const cardsToRender = getCardsForCategory();
  const currentCategoryName =
    categories.find((cat) => cat.id === selectedCategory)?.name || "All";

  return (
    <div className="bg-white">
      <Header />
      <div className="w-full">
        {/* Header with category tabs */}
        <div className="ml-12 flex bg-white shadow-sm">
          <div className="bg-white shadow-sm w-full max-w-[1200px] mx-auto">
            <div className="py-4 overflow-x-auto w-full">
              <div className="flex space-x-2 min-w-max">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => updateURLAndCategory(category.id)}
                    className={`whitespace-nowrap px-[22px] py-2 text-xl font-medium font-manrope font-normal rounded-full transition-colors ${
                      selectedCategory === category.id
                        ? "bg-[#D6F549] text-black"
                        : "text-black hover:text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    {category.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Cards grid */}
        <div className="mt-8 mb-24 flex items-center justify-center">
          {cardsToRender.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[10px]">
              {cardsToRender.map((card: CardData, index: number) => (
                <TalkToAgentCards
                  key={`${currentCategoryName}-${index}`}
                  cardTag={card.cardTag}
                  cardTitle={card.cardTitle}
                  features={card.features}
                  profilePhoto={card.profilePhoto}
                  agentsArray={card.agentsArray}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">
                No agents available for {currentCategoryName}
              </p>
            </div>
          )}
        </div>
      </div>
      <HeroSection /> {/* Place here, outside the constrained container */}
      <Footer />
    </div>
  );
};

export default AgentCardsPage;
